<!-- Inline view: show either list or student details -->
@if (selectedStudent()) {
  <!-- Student Details View -->
  <app-student-result-details
    [student]="selectedStudent()!"
    [resultDetail]="selectedStudentDetail()"
    [isLoading]="isLoadingDetail()"
    [error]="detailError()"
    (back)="backToList()"
  />
} @else {
  <!-- List View -->
  <div class="w-full bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
    <!-- Header -->
    <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-violet-50">
      <div class="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-violet-100 text-violet-600">
        <lucide-icon [img]="icons.BarChart3" class="w-5 h-5"></lucide-icon>
      </div>
      <div class="flex-1 min-w-0">
        <div class="text-sm font-semibold text-gray-900">Grading Results</div>
        <div class="text-xs text-violet-600">{{ getSubtitle() }}</div>
      </div>
    </div>

    <!-- Stats Summary -->
    @if (hasSuccessfulResults()) {
      <div class="px-4 py-3 bg-gray-50/50 border-b border-gray-100">
        <div class="flex items-center gap-4 flex-wrap">
          <!-- Average Grade -->
          @if (averageDisplay(); as avg) {
            <div class="flex items-center gap-2">
              <lucide-icon [img]="icons.Target" class="w-4 h-4 text-violet-500"></lucide-icon>
              <span class="text-xs text-gray-600">
                Average: <span class="font-semibold" [ngClass]="getGradeColorClass(getLetterGrade(avg.percentage))">{{ getLetterGrade(avg.percentage) }}</span>
              </span>
            </div>
          }

          <!-- Failed Count -->
          @if (preview().failedCount > 0) {
            <div class="flex items-center gap-2">
              <lucide-icon [img]="icons.AlertTriangle" class="w-4 h-4 text-red-400"></lucide-icon>
              <span class="text-xs text-red-600">{{ preview().failedCount }} failed</span>
            </div>
          }
        </div>

        <!-- Grade Distribution Chart -->
        @if (gradeDistribution().length > 0) {
          <div class="mt-3">
            <app-grade-distribution-chart
              [distribution]="gradeDistribution()"
            />
          </div>
        }
      </div>
    }

    <!-- Students Section -->
    <div class="border-b border-gray-100">
      <button
        (click)="toggleSection('students')"
        class="w-full px-4 py-2.5 flex items-center gap-2 hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <lucide-icon [img]="icons.Users" class="w-4 h-4 text-violet-500"></lucide-icon>
        <span class="font-medium text-gray-800 text-left text-xs">Student Results</span>
        <span class="text-[10px] text-gray-400">{{ preview().students.length }} students</span>
        <span class="flex-1"></span>
        <span class="text-[10px] text-violet-600 font-medium w-10 text-right">{{ isSectionExpanded('students') ? 'Collapse' : 'Expand' }}</span>
        <lucide-icon
          [img]="icons.ChevronDown"
          class="w-3.5 h-3.5 text-violet-500 transition-transform duration-200 -ml-0.5"
          [class.rotate-180]="isSectionExpanded('students')"
        ></lucide-icon>
      </button>

      @if (isSectionExpanded('students')) {
        <div class="border-t border-gray-100 bg-gray-50/50 divide-y divide-gray-100">
          @for (student of sortedStudents(); track student.studentId) {
            <div class="px-4 py-2.5 flex items-center gap-3">
              <!-- Letter Grade Badge -->
              <span
                class="text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0"
                [ngClass]="{
                  'bg-green-100 text-green-700': getScoreTier(student) === 'high',
                  'bg-yellow-100 text-yellow-700': getScoreTier(student) === 'medium',
                  'bg-orange-100 text-orange-700': getScoreTier(student) === 'low',
                  'bg-red-100 text-red-600': getScoreTier(student) === 'failed'
                }"
              >
                {{ formatStudentScore(student) }}
              </span>

              <!-- Student Name -->
              <span class="text-xs text-gray-700 font-medium flex-1 truncate">{{ student.studentName }}</span>

              <!-- See Details Button -->
              <button
                type="button"
                (click)="selectStudent(student)"
                class="text-[10px] px-2.5 py-1 bg-violet-100 text-violet-700 hover:bg-violet-200 font-medium rounded-full cursor-pointer transition-colors"
              >
                See details
              </button>
            </div>
          }
        </div>
      }
    </div>

    <!-- Question Analysis Section -->
    @if (questionAnalysis(); as analysis) {
      <div class="border-b border-gray-100">
        <button
          (click)="toggleSection('question-analysis')"
          class="w-full px-4 py-2.5 flex items-center gap-2 hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <lucide-icon [img]="icons.PieChart" class="w-4 h-4 text-violet-500"></lucide-icon>
          <span class="font-medium text-gray-800 text-left text-xs">Question Analysis</span>
          <span class="text-[10px] text-gray-400">{{ analysis.sections.length }} sections</span>
          @if (!analysis.aiAnalysisGenerated) {
            <span class="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">AI analyzing...</span>
          }
          <span class="flex-1"></span>
          <span class="text-[10px] text-violet-600 font-medium w-10 text-right">{{ isSectionExpanded('question-analysis') ? 'Collapse' : 'Expand' }}</span>
          <lucide-icon
            [img]="icons.ChevronDown"
            class="w-3.5 h-3.5 text-violet-500 transition-transform duration-200 -ml-0.5"
            [class.rotate-180]="isSectionExpanded('question-analysis')"
          ></lucide-icon>
        </button>

        @if (isSectionExpanded('question-analysis')) {
          <div class="border-t border-gray-100 bg-gray-50/30">
            <app-question-analysis
              [analysis]="analysis"
              [studentFeedbackMap]="studentFeedbackMap()"
              [isLoadingFeedback]="isLoadingStudentFeedback()"
            />
          </div>
        }
      </div>
    }

    <!-- Common Insights Sections -->
    @if (preview().commonStrengths?.length) {
      <div class="border-t border-gray-100">
        <button
          (click)="toggleSection('strengths')"
          class="w-full px-4 py-2.5 flex items-center gap-2 hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <lucide-icon [img]="icons.Lightbulb" class="w-4 h-4 text-green-500"></lucide-icon>
          <span class="font-medium text-gray-800 text-left text-xs">Common Strengths</span>
          <span class="text-[10px] text-gray-400">{{ preview().commonStrengths?.length }} items</span>
          <span class="flex-1"></span>
          <span class="text-[10px] text-violet-600 font-medium w-10 text-right">{{ isSectionExpanded('strengths') ? 'Collapse' : 'Expand' }}</span>
          <lucide-icon
            [img]="icons.ChevronDown"
            class="w-3.5 h-3.5 text-violet-500 transition-transform duration-200 -ml-0.5"
            [class.rotate-180]="isSectionExpanded('strengths')"
          ></lucide-icon>
        </button>

        @if (isSectionExpanded('strengths')) {
          <div class="border-t border-gray-100 bg-green-50/50 px-4 py-2.5">
            <ul class="space-y-1.5">
              @for (strength of preview().commonStrengths; track strength.text) {
                <li class="flex items-start gap-2 text-xs text-gray-700">
                  <lucide-icon [img]="icons.CheckCircle2" class="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5"></lucide-icon>
                  <span>{{ strength.text }}</span>
                  <span class="text-[10px] text-gray-400 ml-auto flex-shrink-0">({{ strength.studentCount }} students)</span>
                </li>
              }
            </ul>
          </div>
        }
      </div>
    }

    @if (preview().commonAreasForImprovement?.length) {
      <div class="border-t border-gray-100">
        <button
          (click)="toggleSection('improvements')"
          class="w-full px-4 py-2.5 flex items-center gap-2 hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <lucide-icon [img]="icons.Target" class="w-4 h-4 text-amber-500"></lucide-icon>
          <span class="font-medium text-gray-800 text-left text-xs">Areas for Improvement</span>
          <span class="text-[10px] text-gray-400">{{ preview().commonAreasForImprovement?.length }} items</span>
          <span class="flex-1"></span>
          <span class="text-[10px] text-violet-600 font-medium w-10 text-right">{{ isSectionExpanded('improvements') ? 'Collapse' : 'Expand' }}</span>
          <lucide-icon
            [img]="icons.ChevronDown"
            class="w-3.5 h-3.5 text-violet-500 transition-transform duration-200 -ml-0.5"
            [class.rotate-180]="isSectionExpanded('improvements')"
          ></lucide-icon>
        </button>

        @if (isSectionExpanded('improvements')) {
          <div class="border-t border-gray-100 bg-amber-50/50 px-4 py-2.5">
            <ul class="space-y-1.5">
              @for (improvement of preview().commonAreasForImprovement; track improvement.text) {
                <li class="flex items-start gap-2 text-xs text-gray-700">
                  <lucide-icon [img]="icons.AlertTriangle" class="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5"></lucide-icon>
                  <span>{{ improvement.text }}</span>
                  <span class="text-[10px] text-gray-400 ml-auto flex-shrink-0">({{ improvement.studentCount }} students)</span>
                </li>
              }
            </ul>
          </div>
        }
      </div>
    }
  </div>
}
